import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Barber, DayOfWeek, ScheduleRequest } from '../../types/barber.types';
import { Branch } from '../../types/branch.types';
import { branchService } from '../../services/branchService';
import { barberService } from '../../services/barberService';
import { useToast } from '../../context/ToastContext';
import AdminHeader from '../../components/admin/AdminHeader';

const DAYS: { key: DayOfWeek; label: string }[] = [
    { key: DayOfWeek.MONDAY, label: 'Lunes' },
    { key: DayOfWeek.TUESDAY, label: 'Martes' },
    { key: DayOfWeek.WEDNESDAY, label: 'Miércoles' },
    { key: DayOfWeek.THURSDAY, label: 'Jueves' },
    { key: DayOfWeek.FRIDAY, label: 'Viernes' },
    { key: DayOfWeek.SATURDAY, label: 'Sábado' },
    { key: DayOfWeek.SUNDAY, label: 'Domingo' },
];

const BarberSchedulePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [barber, setBarber] = useState<Barber | null>(null);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [schedules, setSchedules] = useState<ScheduleRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        if (!id) {
            showToast('ID de barbero no válido', 'error');
            navigate('/admin/barbers');
            return;
        }

        try {
            setLoading(true);

            // Load barber and branches in parallel
            const [barberData, branchesData] = await Promise.all([
                barberService.getBarberById(Number(id)),
                branchService.getAllBranches()
            ]);

            setBarber(barberData);
            setBranches(branchesData);

            // Initialize schedules from barber data
            if (barberData.schedules) {
                setSchedules(barberData.schedules.map(s => ({
                    dayOfWeek: s.dayOfWeek,
                    startTime: s.startTime.substring(0, 5), // Keep HH:mm
                    endTime: s.endTime.substring(0, 5),
                    branchId: s.branchId || 0
                })));
            }
        } catch (error) {
            console.error('Error loading data:', error);
            showToast('Error al cargar los datos', 'error');
            navigate('/admin/barbers');
        } finally {
            setLoading(false);
        }
    };

    const handleAddRange = (day: DayOfWeek) => {
        setSchedules([...schedules, {
            dayOfWeek: day,
            startTime: '10:00',
            endTime: '18:00',
            branchId: branches.length > 0 ? branches[0].id : 0
        }]);
    };

    const handleRemoveRange = (index: number) => {
        const newSchedules = [...schedules];
        newSchedules.splice(index, 1);
        setSchedules(newSchedules);
    };

    const handleUpdateRange = (index: number, updates: Partial<ScheduleRequest>) => {
        const newSchedules = [...schedules];
        newSchedules[index] = { ...newSchedules[index], ...updates };
        setSchedules(newSchedules);
    };

    const handleSave = async () => {
        if (!barber) return;

        if (schedules.some(s => s.branchId === 0)) {
            showToast('Debes seleccionar una sucursal para todos los rangos', 'warning');
            return;
        }

        // Validate no overlapping schedules (frontend validation)
        const overlap = findOverlappingSchedules(schedules);
        if (overlap) {
            showToast(overlap.message, 'error');
            return;
        }

        try {
            setSaving(true);
            console.log('Sending schedules:', schedules);
            await barberService.updateSchedules(barber.id, schedules);
            showToast('Horarios actualizados correctamente', 'success');
            navigate('/admin/barbers');
        } catch (error: any) {
            console.error('Error saving schedules:', error);
            // Show specific error message from backend if available
            const errorMessage = error.response?.data?.message ||
                error.response?.data ||
                error.message ||
                'Error al guardar horarios';
            showToast(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };

    const findOverlappingSchedules = (schedules: ScheduleRequest[]): { message: string } | null => {
        // First, validate that each schedule has valid time range (start < end)
        for (let i = 0; i < schedules.length; i++) {
            const schedule = schedules[i];
            if (schedule.startTime >= schedule.endTime) {
                const dayName = getDayNameInSpanish(schedule.dayOfWeek);
                return {
                    message: `La hora inicial debe ser menor que la hora final para ${dayName} (${schedule.startTime} - ${schedule.endTime})`
                };
            }
        }

        // Then check for overlaps
        for (let i = 0; i < schedules.length; i++) {
            const schedule1 = schedules[i];

            for (let j = i + 1; j < schedules.length; j++) {
                const schedule2 = schedules[j];

                // Only check if same day and same branch
                if (schedule1.dayOfWeek === schedule2.dayOfWeek &&
                    schedule1.branchId === schedule2.branchId) {

                    // Check if time ranges overlap
                    // Two ranges overlap if: start1 < end2 AND start2 < end1
                    if (schedule1.startTime < schedule2.endTime &&
                        schedule2.startTime < schedule1.endTime) {

                        const dayName = getDayNameInSpanish(schedule1.dayOfWeek);
                        return {
                            message: `Los horarios se superponen para ${dayName}: ${schedule1.startTime}-${schedule1.endTime} y ${schedule2.startTime}-${schedule2.endTime}`
                        };
                    }
                }
            }
        }
        return null;
    };

    const getDayNameInSpanish = (dayOfWeek: string): string => {
        const days: Record<string, string> = {
            'MONDAY': 'Lunes',
            'TUESDAY': 'Martes',
            'WEDNESDAY': 'Miércoles',
            'THURSDAY': 'Jueves',
            'FRIDAY': 'Viernes',
            'SATURDAY': 'Sábado',
            'SUNDAY': 'Domingo'
        };
        return days[dayOfWeek] || dayOfWeek;
    };

    if (loading) {
        return (
            <div className="container mt-lg">
                <div className="card p-xl text-center" style={{ background: '#111', border: '1px solid #2a2a2a', color: '#a3a3a3' }}>
                    <div className="spinner m-auto mb-md"></div>
                    <p>Cargando información del barbero...</p>
                </div>
            </div>
        );
    }

    if (!barber) {
        return (
            <div className="container mt-lg">
                <div className="card p-xl text-center" style={{ background: '#111', border: '1px solid #2a2a2a' }}>
                    <p style={{ color: '#ef4444' }} className="mb-md">No se pudo cargar la información del barbero</p>
                    <button className="btn btn-sm" style={{ background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.5rem' }} onClick={() => navigate('/admin/barbers')}>
                        Volver a Gestión de Barberos
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <AdminHeader
                title={`Horarios: ${barber.firstName} ${barber.lastName}`}
                backPath="/admin/barbers"
            />
            <div className="container fade-in">
                <p className="mb-lg" style={{ color: '#f59e0b', fontWeight: '600' }}>
                    "{barber.nickname}"
                </p>

                {/* Info Banner */}
                <div className="card p-md mb-lg" style={{ background: '#f59e0b15', border: '1px solid #f59e0b30', borderLeft: '4px solid #f59e0b', borderRadius: '0.75rem' }}>
                    <p className="text-sm m-0" style={{ color: '#a3a3a3' }}>
                        💡 Configura los rangos horarios y sucursales de trabajo para cada día de la semana.
                    </p>
                </div>

                {/* Schedule Configuration */}
                <div className="card p-lg" style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #2a2a2a', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                    <div className="space-y-lg" style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
                        {DAYS.map(day => (
                            <div
                                key={day.key}
                                style={{
                                    background: '#1a1a1a',
                                    borderRadius: '0.75rem',
                                    padding: '1rem',
                                    border: '1px solid #2a2a2a',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div className="flex justify-between items-center mb-md pb-xs" style={{ borderBottom: '1px solid #2a2a2a' }}>
                                    <h4 className="m-0 font-bold flex items-center gap-xs" style={{ color: '#e5e5e5' }}>
                                        <span style={{
                                            background: '#f59e0b',
                                            color: '#000',
                                            fontSize: '10px',
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: '700'
                                        }}>
                                            {day.label.charAt(0)}
                                        </span>
                                        {day.label.substring(1)}
                                    </h4>
                                    <button
                                        className="btn btn-sm"
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid #f59e0b',
                                            color: '#f59e0b',
                                            borderRadius: '0.5rem',
                                            fontSize: '0.7rem',
                                            padding: '0.3rem 0.75rem',
                                            fontWeight: '600'
                                        }}
                                        onClick={() => handleAddRange(day.key)}
                                        disabled={saving}
                                    >
                                        + Agregar Rango
                                    </button>
                                </div>

                                <div className="space-y-sm">
                                    {schedules.filter(s => s.dayOfWeek === day.key).length === 0 ? (
                                        <p className="text-xs italic text-center py-sm" style={{ color: '#525252' }}>
                                            No trabaja este día
                                        </p>
                                    ) : (
                                        schedules.map((s, idx) => {
                                            if (s.dayOfWeek !== day.key) return null;
                                            return (
                                                <div
                                                    key={idx}
                                                    className="flex flex-wrap items-center gap-sm"
                                                    style={{
                                                        background: '#111',
                                                        padding: '0.5rem',
                                                        borderRadius: '0.5rem',
                                                        border: '1px solid #333'
                                                    }}
                                                >
                                                    <div className="flex items-center gap-xs">
                                                        <span style={{ fontSize: '10px', color: '#737373', fontWeight: '700', textTransform: 'uppercase', width: '24px' }}>De:</span>
                                                        <input
                                                            type="time"
                                                            className="form-input text-sm p-xs"
                                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                                            value={s.startTime}
                                                            onChange={(e) => handleUpdateRange(idx, { startTime: e.target.value })}
                                                            disabled={saving}
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-xs">
                                                        <span style={{ fontSize: '10px', color: '#737373', fontWeight: '700', textTransform: 'uppercase', width: '24px' }}>A:</span>
                                                        <input
                                                            type="time"
                                                            className="form-input text-sm p-xs"
                                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                                            value={s.endTime}
                                                            onChange={(e) => handleUpdateRange(idx, { endTime: e.target.value })}
                                                            disabled={saving}
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-[150px]">
                                                        <select
                                                            className="form-input text-sm p-xs"
                                                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                                            value={s.branchId}
                                                            onChange={(e) => handleUpdateRange(idx, { branchId: Number(e.target.value) })}
                                                            disabled={saving}
                                                        >
                                                            <option value={0}>Seleccionar Sucursal</option>
                                                            {branches.map(b => (
                                                                <option key={b.id} value={b.id}>{b.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <button
                                                        className="btn btn-sm"
                                                        style={{ background: 'transparent', border: 'none', color: '#ef4444', padding: '0.25rem' }}
                                                        onClick={() => handleRemoveRange(idx)}
                                                        disabled={saving}
                                                        title="Eliminar"
                                                    >
                                                        ❌
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Action Buttons - Sticky at the bottom of the card */}
                    <div style={{
                        position: 'sticky',
                        bottom: '-1.5rem',
                        left: 0,
                        right: 0,
                        backgroundColor: '#111111',
                        display: 'flex',
                        justifyContent: 'end',
                        gap: '1rem',
                        marginTop: '1.5rem',
                        paddingTop: '1rem',
                        paddingBottom: '0.5rem',
                        borderTop: '1px solid #2a2a2a',
                        zIndex: 10,
                        borderRadius: '0 0 0.75rem 0.75rem'
                    }}>
                        <button
                            className="btn px-xl"
                            style={{
                                background: 'transparent',
                                border: '1px solid #404040',
                                color: '#a3a3a3',
                                borderRadius: '0.75rem',
                                fontWeight: '600'
                            }}
                            onClick={() => navigate('/admin/barbers')}
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn px-xl"
                            style={{
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                border: 'none',
                                color: '#000',
                                borderRadius: '0.75rem',
                                fontWeight: '700',
                                boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)'
                            }}
                            onClick={handleSave}
                            disabled={saving || loading}
                        >
                            {saving ? 'Guardando...' : 'Guardar Horarios'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BarberSchedulePage;
